import React, { useState, useEffect, useMemo } from "react";
import { Select, Table, Tag, Modal, Button, Pagination, Input } from "antd";
import Chart from "react-apexcharts";
import societies_icn from "../../assets/images/societies-icn.svg";
import society_icn from "../../assets/images/society_icn.png";
import revenue_icn from "../../assets/images/revenue.svg";
import members_icn from "../../assets/images/members.svg";
import registrations_icn from "../../assets/images/registrations.svg";
import switch_icn from "../../assets/images/switch.png";
import Header from "../common/Header/index";
import "./dashboard.scss";
import moment from "moment";
import { Cookies } from "react-cookie";

const DashboardComponent = (props) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");

  const {
    societyListData,
    callsocietyListData,
    society_count,
    dashboardActivities,
    society_status_counts,
    society_users_counts,
  } = props;
  const cookies = new Cookies();

  const totalSocietyList = societyListData?.societyListData?.data;

  useEffect(() => {
    const validateToken = async () => {
      const token = cookies.get("token");
      if (token) {
        await callsocietyListData({
          type: null,
          startDate: null,
          endDate: null,
        });
      }
    };
    validateToken();
  }, [callsocietyListData]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const helpDeskDonutSeries = [
    society_count?.resolved_help_desk_tickets || 0,
    society_count?.in_progress_help_desk_tickets || 0,
    society_count?.pending_help_desk_tickets || 0,
  ];
  const helpDeskDonutOptions = {
    chart: {
      type: "donut",
      height: 220,
    },

    labels: ["Resolved", "In-Progress", "Pending"],
    
    colors: ["#52c41a", "#1890ff", "#ee5766"], 
    plotOptions: {
      pie: {
        donut: {
          size: "65%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Tickets",
              fontSize: "14px",
              color: "#6b6b6b",
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false, 
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val + " tickets";
        },
      },
    },
  };

  const societies_data = {
    chart: {
      id: "line-chart",
      toolbar: {
        show: false,
      },
    },
    colors: ["#d6ad61"],
    stroke: {
      curve: "smooth",
      width: 4,
    },
    markers: {
      size: 6,
      colors: ["#d6ad61"],
      hover: {
        size: 8,
      },
    },
    grid: {
      show: true,
    },
    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sept",
        "Oct",
        "Nov",
        "Dec",
      ],
    },
    yaxis: {
      min: 0,
      max: 200,
    },
    tooltip: {
      shared: false,
      intersect: true,
    },
  };

  const societies_series = [
    {
      name: "New Registration",
      data: society_status_counts,
    },
  ];

  const revenue_data = {
    chart: {
      id: "bar-chart",
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        borderRadius: 5,
        columnWidth: "45%",
      },
    },
    xaxis: {
      categories: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sept",
        "Oct",
        "Nov",
        "Dec",
      ],
      labels: {
        style: {
          colors: "#a0a0a0",
          fontSize: "12px",
        },
      },
    },
    yaxis: {
      tickAmount: 5,
      min: 0,
      max: 200,
      labels: {
        style: {
          colors: "#a0a0a0",
          fontSize: "12px",
        },
      },
    },
    grid: {
      show: true,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      colors: ["#d6ad61"],
    },
  };

  const revenue_series = [
    {
      name: "New Users",
      data: society_users_counts,
    },
  ];

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const filteredNotifications = useMemo(() => {
    if (!searchQuery) {
      return dashboardActivities || [];
    }
    const query = searchQuery.toLowerCase();
    return (dashboardActivities || []).filter((item) => {
      const title = (item.title || "").toLowerCase();
      const description = (item.description || "").toLowerCase();
      return title.includes(query) || description.includes(query);
    });
  }, [dashboardActivities, searchQuery]);

  const paginatedNotifications = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredNotifications.slice(startIndex, endIndex);
  }, [filteredNotifications, currentPage, pageSize]);

  const societies_table_data = totalSocietyList?.map((item, index) => ({
    key: index + 1,
    societyName: item.name,
    joinDate: new Date(item.createdAt).toLocaleDateString(),
    admin: item.president?.name || "N/a",
    contactNumber: item.president.phone_number || "N/a",
    emailAddress: item.president?.email || "N/a",
    wings: item.wingCount || "N/a",
    type: item.type || "N/a",
    status:
      (item.status || "Inactive").charAt(0).toUpperCase() +
      (item.status || "Inactive").slice(1),
  }));

  const societies_table_columns = [
    {
      title: "Sr. No.",
      dataIndex: "key",
      key: "key",
      render: (text) => <span>{text.toString().padStart(2, "0")}</span>,
    },
    {
      title: "Society Name",
      dataIndex: "societyName",
      key: "societyName",
      render: (name) =>
        name
          ? name
              .split(" ")
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              )
              .join(" ")
          : "",
    },
    {
      title: "Join Date",
      dataIndex: "joinDate",
      key: "joinDate",
    },
    {
      title: "Admin",
      dataIndex: "admin",
      key: "admin",
      render: (name) =>
        name
          ? name
              .split(" ")
              .map(
                (word) =>
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              )
              .join(" ")
          : "",
    },
    {
      title: "Phone Number",
      dataIndex: "contactNumber",
      key: "contactNumber",
    },
    {
      title: "Email Address",
      dataIndex: "emailAddress",
      key: "emailAddress",
    },
    {
      title: "Wings",
      dataIndex: "wings",
      key: "wings",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (priority) =>
        priority
          ? priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase()
          : "",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "red"}>{status}</Tag>
      ),
    },
  ];

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setSearchQuery("");
  };

  const handlePageChange = (page, newPageSize) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  return (
    <>
      <div className="dashboard-main">
        <Header title="Dashboard" />
        <div className="dashboard-first">
          <div className="overview">
            <div className="overview-top">
              <div className="top-left-section">
                <span className="first-text">Key Metrics Overview</span>
                <span className="second-text">
                  Get a snapshot of your society’s current status with these key
                  metrics. Track performance, engagement, and overall health of
                  your society, enabling data-driven decisions for improvements
                  and growth.
                </span>
              </div>
              <div className="top-right-section">
                <Select
                  showSearch
                  className="select-month"
                  placeholder="This Month"
                  optionFilterProp="children"
                  defaultValue={"This Month"}
                  disabled
                />
              </div>
            </div>
            <div className="overview-bottom">
              <div className="card-1 card">
                <img src={societies_icn} alt="Total Societies Icon" />
                <h3>
                  {society_count?.total_society_count
                    ? society_count.total_society_count
                    : 0}
                </h3>{" "}
                <p>Total Societies</p>
                <span
                  className={
                    society_count?.this_month_society_register_change_percentage >
                    0
                      ? "green-text"
                      : "red-text"
                  }
                >
                  {society_count?.this_month_society_register_change_percentage
                    ? parseFloat(
                        Number(
                          society_count.this_month_society_register_change_percentage
                        ).toFixed(2)
                      )
                    : 0}
                  % From Last Month
                </span>
              </div>

              <div className="card-2 card">
                <img src={revenue_icn} alt="New Societies Icon" />
                <h3>
                  {" "}
                  {society_count?.this_month_society_register_count
                    ? society_count.this_month_society_register_count
                    : 0}
                </h3>
                <p>New Societies</p>
                <span
                  className={
                    society_count?.this_month_society_register_change_percentage >
                    0
                      ? "green-text"
                      : "red-text"
                  }
                >
                  {society_count?.this_month_society_register_change_percentage
                    ? parseFloat(
                        Number(
                          society_count.this_month_society_register_change_percentage
                        ).toFixed(2)
                      )
                    : 0}
                  % From Last Month
                </span>
              </div>

              <div className="card-3 card">
                <img src={members_icn} alt="Active Members Icon" />
                <h3>
                  {society_count?.total_Active_member_count
                    ? society_count?.total_Active_member_count
                    : 0}
                </h3>
                <p>Active Members</p>
                <span
                  className={
                    society_count?.this_month_active_members_change_percentage >
                    0
                      ? "green-text"
                      : "red-text"
                  }
                >
                  {society_count?.this_month_active_members_change_percentage
                    ? parseFloat(
                        Number(
                          society_count.this_month_active_members_change_percentage
                        ).toFixed(2)
                      )
                    : 0}
                  % From Last Month
                </span>
              </div>

              <div className="card-4 card">
                <img src={registrations_icn} alt="New Users Icon" />
                <h3>
                  {society_count?.this_month_user_register_count
                    ? society_count?.this_month_user_register_count
                    : 0}
                </h3>{" "}
                <p> New Users </p>
                <span
                  className={
                    society_count?.this_month_user_register_change_percentage >
                    0
                      ? "green-text"
                      : "red-text"
                  }
                >
                  {society_count?.this_month_user_register_change_percentage
                    ? parseFloat(
                        Number(
                          society_count.this_month_user_register_change_percentage
                        ).toFixed(2)
                      )
                    : 0}
                  % From Last Month
                </span>
              </div>
            </div>
          </div>
          <div className="complaint-section">
            <div className="complaint-top">
              <p>Help Desk Tickets</p>
            </div>
            <div className="complaint-bottom">
              <div className="left">
                {/* --- UPDATE THIS CHART COMPONENT --- */}
                <Chart
                  options={helpDeskDonutOptions}
                  series={helpDeskDonutSeries}
                  type="donut"
                  width={"100%"}
                  height={"220px"}
                />
              </div>
              <div className="right">
                <div className="right-top">
                  <p>
                    {society_count?.resolved_help_desk_tickets
                      ? society_count?.resolved_help_desk_tickets
                      : 0}
                  </p>
                  <span>Resolved Tickets</span>
                </div>
                <div className="right-middle">
                  <p>
                    {society_count?.in_progress_help_desk_tickets
                      ? society_count?.in_progress_help_desk_tickets
                      : 0}
                  </p>
                  <span>In-Progress Tickets</span>
                </div>
                <div className="right-bottom">
                  <p>
                    {society_count?.pending_help_desk_tickets
                      ? society_count?.pending_help_desk_tickets
                      : 0}
                  </p>
                  <span>Pending Tickets</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="dashboard-second">
          <div className="societies-section">
            <div className="societies-top">
              <p>Total Societies</p>
            </div>
            <div className="societies-bottom">
              <Chart
                type="line"
                options={societies_data}
                series={societies_series}
                width={"100%"}
                height={"280px"}
              />
            </div>
          </div>
          <div className="revenue-section">
            <div className="revenue-top">
              <p>Total Users</p>
              <div className="btn-section"></div>
            </div>
            <div className="revenue-bottom">
              <Chart
                options={revenue_data}
                series={revenue_series}
                type="bar"
                height="275"
              />
            </div>
          </div>
          <div className="requests-section">
            <div className="requests-top">
              <p>Notifications</p>
              <Button type="link" onClick={showModal}>
                View All
              </Button>
            </div>
            <div className="requests-bottom">
              {dashboardActivities?.slice(0, 10).map((item) => {
                const date = new Date(item.ts * 1000);
                const formattedTime = date.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                });
                return (
                  <div
                    className="req-first req-global"
                    key={item._id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "10px",
                      padding: "10px",
                      borderRadius: "8px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      className="img-cont"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        backgroundColor:
                          item.sub_type === "SOCIETY_REGISTERED"
                            ? "#FFE2E599"
                            : item.sub_type === "PRESIDENT_ADDED"
                            ? "#F7EFFF"
                            : item.sub_type === "PRESIDENT_SWITCHED"
                            ? "#dcfce799"
                            : "#FFE2E599",
                        borderRadius: "50%",
                        marginRight: "15px",
                        flexShrink: 0,
                      }}
                    >
                      {item.sub_type === "SOCIETY_REGISTERED" ? (
                        <img
                          src={society_icn}
                          alt="Society"
                          style={{ width: "45px", height: "45px" }}
                        />
                      ) : item.sub_type === "PRESIDENT_ADDED" ? (
                        <img
                          src={registrations_icn}
                          alt="President"
                          style={{ width: "45px", height: "45px" }}
                        />
                      ) : item.sub_type === "PRESIDENT_SWITCHED" ? (
                        <img
                          src={switch_icn}
                          alt="President Switched"
                          style={{ width: "45px", height: "45px" }}
                        />
                      ) : (
                        <img
                          src={registrations_icn}
                          alt="Default"
                          style={{ width: "45px", height: "45px" }}
                        />
                      )}
                    </div>
                    <div className="request-text" style={{ flexGrow: 1 }}>
                      <p
                        className="req-title"
                        style={{
                          margin: 0,
                          fontSize: "16px",
                          fontWeight: "bold",
                          color: "#333",
                        }}
                      >
                        {item.title}
                      </p>
                      <p
                        className="req-desc"
                        style={{
                          margin: "5px 0 0",
                          fontSize: "14px",
                          color: "#666",
                        }}
                      >
                        {item.description}
                      </p>
                      <p
                        className="req-discription"
                        style={{
                          margin: "5px 0 0",
                          fontSize: "12px",
                          color: "#999",
                        }}
                      >
                        {moment(item.createdAt).format("DD/MM/YYYY")}
                        {",    "}
                        {formattedTime}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="dashboard-third">
          <div className="table-top">
            <p>Society Overview</p>
          </div>
          <div className="table-bottom">
            <Table
              dataSource={societies_table_data}
              columns={societies_table_columns}
              pagination={{
                pageSize: 5,
                position: ["bottomCenter"],
                showSizeChanger: false,
              }}
              scroll={{ x: "max-content" }}
              className="society-table"
            />
          </div>
        </div>
      </div>

      <Modal
        title="All Notifications"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width="80%"
        style={{ top: 40 }}
        styles={{
          content: {
            padding: 0,
            maxHeight: "80vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: "8px",
            overflow: "hidden",
          },
          header: {
            backgroundColor: "#f0f2f5",
            padding: "16px 24px",
            borderBottom: "1px solid #e8e8e8",
          },
          body: {
            padding: "20px",
            flexGrow: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Input.Search
          placeholder="Search by title or description..."
          value={searchQuery}
          onChange={handleSearchChange}
          style={{ marginBottom: "20px", flexShrink: 0 }}
          allowClear
        />

        <div
          className="all-notifications-modal-content"
          style={{ overflowY: "auto", flexGrow: 1 }}
        >
          {paginatedNotifications.length > 0 ? (
            paginatedNotifications.map((item) => {
              const date = new Date(item.ts * 1000);
              const formattedTime = date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              });
              return (
                <div
                  className="req-first req-global"
                  key={item._id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                    padding: "10px",
                    border: "1px solid #e8e8e8",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div
                    className="img-cont"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "70px",
                      height: "70px",
                      backgroundColor:
                        item.sub_type === "SOCIETY_REGISTERED"
                          ? "#FFE2E599"
                          : item.sub_type === "PRESIDENT_ADDED"
                          ? "#F7EFFF"
                          : item.sub_type === "PRESIDENT_SWITCHED"
                          ? "#dcfce799"
                          : "#FFE2E599",
                      borderRadius: "50%",
                      marginRight: "15px",
                      flexShrink: 0,
                    }}
                  >
                    {item.sub_type === "SOCIETY_REGISTERED" ? (
                      <img
                        src={society_icn}
                        alt="Society"
                        style={{ width: "45px", height: "45px" }}
                      />
                    ) : item.sub_type === "PRESIDENT_ADDED" ? (
                      <img
                        src={registrations_icn}
                        alt="President"
                        style={{ width: "45px", height: "45px" }}
                      />
                    ) : item.sub_type === "PRESIDENT_SWITCHED" ? (
                      <img
                        src={switch_icn}
                        alt="Switched"
                        style={{ width: "45px", height: "45px" }}
                      />
                    ) : (
                      <img
                        src={registrations_icn}
                        alt="Default"
                        style={{ width: "45px", height: "45px" }}
                      />
                    )}
                  </div>
                  <div className="request-text" style={{ flexGrow: 1 }}>
                    <p
                      className="req-title"
                      style={{
                        margin: 0,
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#333",
                      }}
                    >
                      {item.title}
                    </p>
                    <p
                      className="req-desc"
                      style={{
                        margin: "5px 0 0",
                        fontSize: "14px",
                        color: "#666",
                      }}
                    >
                      {item.description}
                    </p>
                    <p
                      className="req-discription"
                      style={{
                        margin: "5px 0 0",
                        fontSize: "12px",
                        color: "#999",
                      }}
                    >
                      {moment(item.createdAt).format("DD/MM/YYYY")}
                      {",    "}
                      {formattedTime}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <div
              style={{ textAlign: "center", color: "#999", marginTop: "20px" }}
            >
              No notifications found.
            </div>
          )}
        </div>

        {filteredNotifications.length > 0 && (
          <div
            style={{
              padding: "16px 0 0",
              textAlign: "center",
              flexShrink: 0,
              borderTop: "1px solid #e8e8e8",
              marginTop: "auto",
            }}
          >
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredNotifications.length}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default DashboardComponent;
